import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsPage } from '../components/SettingsPage';
import { HelpAside } from '../components/SettingsAsides';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { disablePush, enablePush, pushState, type PushState } from '../lib/push';
import { PageHeader, Section } from '../components/forms';
import { TwoFactorSetup } from '../components/TwoFactor';
import { Button, ErrorNote, Field } from '../components/ui';
import { api, authClient, type Me } from '../lib/api';
import { useAuthError } from '../lib/auth-errors';
import { setLocale, useLocale, useT } from '../lib/i18n';

/** Salon → Je account: two-step verification, password, where you're logged in. */
export function MyAccountPage() {
  const { orgId } = useParams({ from: '/s/$orgId' });
  const t = useT();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/me') });
  const owner = !!me.data?.salons.find((s) => s.orgId === orgId)?.twoFactorRequired;
  const required = !!me.data?.isAdmin || owner;
  if (!me.data) return <p className="text-muted">{t('common.loading')}</p>;
  return (
    <SettingsPage orgId={orgId} aside={<HelpAside slugs={['tweestapsverificatie', 'agenda-in-je-telefoon']} />}>
      <PageHeader orgId={orgId} title={t('acc.title')} intro={t('acc.intro')} />
      <p className="-mt-2 text-sm text-muted">
        {me.data.user.name} · {me.data.user.email}
      </p>
      <Section title={t('tf.title')} intro={t('tf.intro')}>
        <TwoFactorSetup enabled={me.data.user.twoFactorEnabled} required={required} requiredNote={me.data.isAdmin ? t('tf.requiredTeam') : t('tf.requiredOff')} />
      </Section>
      <PushSection />
      <PasswordSection />
      <DevicesSection />
      <SessionSection />
    </SettingsPage>
  );
}

function PasswordSection() {
  const t = useT();
  const authError = useAuthError();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    setBusy(true);
    setError(null);
    setDone(false);
    const { error: err } = await authClient.changePassword({ currentPassword: String(f.get('current')), newPassword: String(f.get('new')), revokeOtherSessions: true });
    setBusy(false);
    if (err) return setError(authError(err));
    form.reset();
    setDone(true);
    void qc.invalidateQueries({ queryKey: ['sessions'] });
  }
  return (
    <Section title={t('pw.title')}>
      <form onSubmit={submit} className="flex max-w-sm flex-col gap-3">
        <Field id="pw-current" name="current" type="password" autoComplete="current-password" required label={t('pw.current')} />
        <Field id="pw-new" name="new" type="password" autoComplete="new-password" minLength={10} required label={t('pw.new')} hint={t('field.passwordHint')} />
        <ErrorNote message={error} />
        {done && <p className="text-sm font-medium text-accent-strong">✓ {t('pw.changed')}</p>}
        <Button type="submit" variant="secondary" disabled={busy} className="self-start">
          {t('pw.change')}
        </Button>
      </form>
    </Section>
  );
}

/** "iPhone · Safari" from a user agent; good enough to recognise your own devices. */
function deviceName(ua: string | null | undefined) {
  if (!ua) return null;
  const os = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) ? 'iPad' : /Android/.test(ua) ? 'Android' : /Mac OS X|Macintosh/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : null;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : null;
  return [os, browser].filter(Boolean).join(' · ') || null;
}

function DevicesSection() {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: async () => {
      const [list, current] = await Promise.all([authClient.listSessions(), authClient.getSession()]);
      return { list: list.data ?? [], current: current.data?.session.token ?? null };
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['sessions'] });
  const revoke = useMutation({ mutationFn: (token: string) => authClient.revokeSession({ token }), onSuccess: refresh });
  const others = useMutation({ mutationFn: () => authClient.revokeOtherSessions(), onSuccess: refresh });
  const when = (d: Date | string) => new Intl.DateTimeFormat(locale === 'nl' ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(d));
  const list = [...(sessions.data?.list ?? [])].sort((a, b) => (a.token === sessions.data?.current ? -1 : b.token === sessions.data?.current ? 1 : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()));
  return (
    <Section title={t('dev.title')} intro={t('dev.intro')}>
      <ul className="flex flex-col divide-y divide-line">
        {list.map((s) => {
          const mine = s.token === sessions.data?.current;
          return (
            <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
              <div>
                <p className="font-medium">
                  {deviceName(s.userAgent) ?? t('dev.unknown')}
                  {mine && <span className="ml-2 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold text-accent-strong">{t('dev.this')}</span>}
                </p>
                <p className="text-sm text-muted">{t('dev.active', { when: when(s.updatedAt) })}</p>
              </div>
              {!mine && (
                <Button variant="ghost" className="min-h-9 px-3 text-sm" onClick={() => revoke.mutate(s.token)} disabled={revoke.isPending}>
                  {t('dev.signOut')}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {list.length > 1 && (
        <Button variant="secondary" className="self-start" onClick={() => others.mutate()} disabled={others.isPending}>
          {t('dev.signOutOthers')}
        </Button>
      )}
    </Section>
  );
}

/** Language and logging out (on desktop these live behind your initials in the rail). */
function SessionSection() {
  const t = useT();
  const locale = useLocale();
  const qc = useQueryClient();
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" onClick={() => setLocale(locale === 'nl' ? 'en' : 'nl')}>
        {t('account.language')}
      </Button>
      <Button
        variant="secondary"
        onClick={async () => {
          await authClient.signOut();
          qc.clear();
          navigate({ to: '/inloggen' });
        }}
      >
        {t('account.logout')}
      </Button>
    </div>
  );
}

/** Push notifications on this device: new online bookings, client cancellations and moves. */
function PushSection() {
  const t = useT();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    void pushState().then(setState, () => setState('unsupported'));
  }, []);
  const change = (fn: () => Promise<PushState>) => async () => {
    setBusy(true);
    setError(null);
    try {
      setState(await fn());
    } catch {
      setError(t('error.generic'));
    } finally {
      setBusy(false);
    }
  };
  if (!state) return null;
  return (
    <Section title={t('push.title')} intro={t('push.intro')}>
      {state === 'on' && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="font-medium">✓ {t('push.on')}</p>
          <Button variant="secondary" onClick={change(disablePush)} disabled={busy}>
            {t('push.disable')}
          </Button>
        </div>
      )}
      {state === 'off' && (
        <Button onClick={change(() => enablePush(t('push.confirmTitle'), t('push.confirmBody')))} disabled={busy} className="self-start">
          {t('push.enable')}
        </Button>
      )}
      {state === 'blocked' && <p className="text-sm text-warning">{t('push.blocked')}</p>}
      {state === 'needs-home-screen' && <p className="text-sm text-muted">{t('push.homeScreen')}</p>}
      {state === 'unsupported' && <p className="text-sm text-muted">{t('push.unsupported')}</p>}
      <ErrorNote message={error} />
    </Section>
  );
}
